/**
 * ReceptionCalendarView Component
 * Calendar view for reception appointments using FullCalendar
 *
 * @version 2025.1.0
 */

import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Stack,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Login as CheckInIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  Cancel as NoShowIcon,
} from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { EventClickArg, EventInput } from '@fullcalendar/core';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';

import {
  ReceptionAppointment,
  AttendanceStatus,
  ATTENDANCE_STATUS_LABELS,
  ATTENDANCE_STATUS_COLORS,
  formatQueueNumber,
  formatTime,
  getAvailableActions,
} from '../../../types/reception';
import { PrerequisiteBlock, type PrerequisiteBlockProps } from '../../../components/common';

// Color mapping for calendar events
const STATUS_EVENT_COLORS: Record<AttendanceStatus, { backgroundColor: string; borderColor: string; textColor: string }> = {
  [AttendanceStatus.NOT_ATTENDED]: { backgroundColor: '#9e9e9e', borderColor: '#757575', textColor: '#fff' },
  [AttendanceStatus.CHECKED_IN]: { backgroundColor: '#2196f3', borderColor: '#1976d2', textColor: '#fff' },
  [AttendanceStatus.TRIAGED]: { backgroundColor: '#9c27b0', borderColor: '#7b1fa2', textColor: '#fff' },
  [AttendanceStatus.IN_PROGRESS]: { backgroundColor: '#ff9800', borderColor: '#f57c00', textColor: '#fff' },
  [AttendanceStatus.COMPLETED]: { backgroundColor: '#4caf50', borderColor: '#388e3c', textColor: '#fff' },
  [AttendanceStatus.NO_SHOW]: { backgroundColor: '#f44336', borderColor: '#d32f2f', textColor: '#fff' },
};

interface ReceptionCalendarViewProps {
  appointments: ReceptionAppointment[];
  loading?: boolean;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  onCheckIn: (appointmentId: number) => void;
  onStartAttendance: (appointmentId: number) => void;
  // Devolve o bloco de pré-requisito quando a conclusão esbarra em algo que falta (ex.: laudo
  // de exame complementar pendente); null quando concluiu ou falhou por outro motivo.
  onCompleteAttendance: (appointmentId: number) => Promise<PrerequisiteBlockProps | null>;
  onMarkNoShow: (appointmentId: number) => void;
}

export const ReceptionCalendarView: React.FC<ReceptionCalendarViewProps> = ({
  appointments,
  loading = false,
  selectedDate,
  onDateChange,
  onCheckIn,
  onStartAttendance,
  onCompleteAttendance,
  onMarkNoShow,
}) => {
  const [selectedEvent, setSelectedEvent] = useState<ReceptionAppointment | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prerequisite, setPrerequisite] = useState<PrerequisiteBlockProps | null>(null);

  // Convert appointments to FullCalendar events
  const events: EventInput[] = useMemo(() => {
    return appointments.map((apt) => {
      const colors = STATUS_EVENT_COLORS[apt.attendanceStatus] || STATUS_EVENT_COLORS[AttendanceStatus.NOT_ATTENDED];
      const time = formatTime(apt.hour, apt.minute);
      const queueNum = apt.queueNumber ? ` #${formatQueueNumber(apt.queueNumber)}` : '';

      // Parse the date and time to create event start
      let startDate: Date;
      if (apt.selectedDate) {
        // selectedDate could be in format YYYY-MM-DD or DD/MM/YYYY
        const dateParts = apt.selectedDate.includes('-')
          ? apt.selectedDate.split('-')
          : apt.selectedDate.split('/').reverse();

        const year = parseInt(dateParts[0] || '2025');
        const month = parseInt(dateParts[1] || '1') - 1;
        const day = parseInt(dateParts[2] || '1');
        const hour = parseInt(apt.hour ?? '0');
        const minute = parseInt(apt.minute ?? '0');

        startDate = new Date(year, month, day, hour, minute);
      } else {
        startDate = new Date();
      }

      // End time is 30 minutes after start
      const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);

      return {
        id: String(apt.id),
        title: `${queueNum} ${apt.employeeName ?? 'Paciente'}`,
        start: startDate,
        end: endDate,
        backgroundColor: colors.backgroundColor,
        borderColor: colors.borderColor,
        textColor: colors.textColor,
        extendedProps: {
          appointment: apt,
          time,
          status: apt.attendanceStatus,
        },
      };
    });
  }, [appointments]);

  // Handle event click
  const handleEventClick = useCallback((info: EventClickArg) => {
    const appointment = info.event.extendedProps.appointment as ReceptionAppointment;
    setSelectedEvent(appointment);
    setPrerequisite(null);
    setDialogOpen(true);
  }, []);

  // Handle date navigation
  const handleDatesSet = useCallback((dateInfo: { start: Date; end: Date; view: { type: string } }) => {
    // Get the center date of the view
    const centerDate = new Date(dateInfo.start.getTime() + (dateInfo.end.getTime() - dateInfo.start.getTime()) / 2);
    onDateChange(centerDate);
  }, [onDateChange]);

  // Close dialog
  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedEvent(null);
    setPrerequisite(null);
  }, []);

  // Action handlers
  const handleAction = useCallback(async (action: string) => {
    if (!selectedEvent) return;

    if (action === 'complete-attendance') {
      const blocked = await onCompleteAttendance(selectedEvent.id);
      if (blocked) {
        // Mantém o diálogo aberto para mostrar o bloqueio em vez de fechar como se tivesse concluído.
        setPrerequisite(blocked);
        return;
      }
      handleCloseDialog();
      return;
    }

    switch (action) {
      case 'check-in':
        onCheckIn(selectedEvent.id);
        break;
      case 'start-attendance':
        onStartAttendance(selectedEvent.id);
        break;
      case 'no-show':
        onMarkNoShow(selectedEvent.id);
        break;
    }
    handleCloseDialog();
  }, [selectedEvent, onCheckIn, onStartAttendance, onCompleteAttendance, onMarkNoShow, handleCloseDialog]);

  // Get available actions for selected event
  const availableActions = selectedEvent ? getAvailableActions(selectedEvent.attendanceStatus) : [];

  return (
    <Box sx={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="timeGridDay"
        initialDate={selectedDate}
        locale={ptBrLocale}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'timeGridDay,timeGridWeek,dayGridMonth',
        }}
        buttonText={{
          today: 'Hoje',
          day: 'Dia',
          week: 'Semana',
          month: 'Mes',
        }}
        events={events}
        eventClick={handleEventClick}
        datesSet={handleDatesSet}
        slotMinTime="06:00:00"
        slotMaxTime="22:00:00"
        slotDuration="00:30:00"
        allDaySlot={false}
        nowIndicator={true}
        height="auto"
        aspectRatio={1.8}
        expandRows={true}
        stickyHeaderDates={true}
        dayMaxEvents={true}
        eventDisplay="block"
        eventTimeFormat={{
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }}
      />

      {/* Event Details Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Detalhes do Agendamento</Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {prerequisite && (
            <Box sx={{ mb: 2 }}>
              <PrerequisiteBlock {...prerequisite} />
            </Box>
          )}
          {selectedEvent && (
            <Stack spacing={2}>
              {/* Queue Number and Status */}
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" color="primary">
                  {selectedEvent.queueNumber ? `#${formatQueueNumber(selectedEvent.queueNumber)}` : '-'}
                </Typography>
                <Chip
                  label={ATTENDANCE_STATUS_LABELS[selectedEvent.attendanceStatus]}
                  color={ATTENDANCE_STATUS_COLORS[selectedEvent.attendanceStatus]}
                  size="medium"
                />
              </Box>

              <Divider />

              {/* Patient Info */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Paciente</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedEvent.employeeName ?? '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CPF: {selectedEvent.employeeCpf ?? '-'}
                </Typography>
              </Box>

              {/* Company */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Empresa</Typography>
                <Typography variant="body1">{selectedEvent.companyName ?? '-'}</Typography>
              </Box>

              {/* Appointment Info */}
              <Box display="flex" gap={4}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Horário</Typography>
                  <Typography variant="body1">
                    {formatTime(selectedEvent.hour, selectedEvent.minute)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Tipo de Exame</Typography>
                  <Typography variant="body1">{selectedEvent.examType ?? '-'}</Typography>
                </Box>
              </Box>

              {/* Clinic */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Clínica</Typography>
                <Typography variant="body1">{selectedEvent.clinicName ?? '-'}</Typography>
              </Box>

              {/* Timestamps */}
              {(selectedEvent.presenceConfirmedAt || selectedEvent.attendanceStartedAt || selectedEvent.attendanceCompletedAt) && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Histórico
                    </Typography>
                    <Stack spacing={0.5}>
                      {selectedEvent.presenceConfirmedAt && (
                        <Typography variant="body2">
                          Check-in: {new Date(selectedEvent.presenceConfirmedAt).toLocaleString('pt-BR')}
                        </Typography>
                      )}
                      {selectedEvent.attendanceStartedAt && (
                        <Typography variant="body2">
                          Início: {new Date(selectedEvent.attendanceStartedAt).toLocaleString('pt-BR')}
                        </Typography>
                      )}
                      {selectedEvent.attendanceCompletedAt && (
                        <Typography variant="body2">
                          Conclusao: {new Date(selectedEvent.attendanceCompletedAt).toLocaleString('pt-BR')}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          {availableActions.includes('check-in') && (
            <Button
              variant="contained"
              color="info"
              startIcon={<CheckInIcon />}
              onClick={() => handleAction('check-in')}
            >
              Check-in
            </Button>
          )}
          {availableActions.includes('start-attendance') && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<StartIcon />}
              onClick={() => handleAction('start-attendance')}
            >
              Iniciar
            </Button>
          )}
          {availableActions.includes('complete-attendance') && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CompleteIcon />}
              onClick={() => handleAction('complete-attendance')}
            >
              Concluir
            </Button>
          )}
          {availableActions.includes('no-show') && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<NoShowIcon />}
              onClick={() => handleAction('no-show')}
            >
              Faltou
            </Button>
          )}
          <Button onClick={handleCloseDialog} color="inherit">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceptionCalendarView;
